import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Mail, ArrowLeft, RefreshCw, Save, Play, Settings, Inbox,
  CheckCircle, XCircle, Loader2, FileText, Folder, FolderOpen,
  Plus, Trash2, AlertCircle, Clock, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailIngestionPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  
  // Config state
  const [config, setConfig] = useState({
    enabled: false,
    folder_to_monitor: 'INBOX',
    processed_folder: 'Processed',
    error_folder: 'IngestionErrors',
    auto_classify: true,
    default_study_id: null,
    default_folder_id: null,
    subject_patterns: [],
    allowed_senders: [],
    auto_workflow_status: 'draft',
    notify_on_ingestion: true
  });
  
  // Stats and logs
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [targets, setTargets] = useState({ studies: [], folders_by_study: {} });
  
  // Pattern dialog
  const [showPatternDialog, setShowPatternDialog] = useState(false);
  const [newPattern, setNewPattern] = useState({ pattern: '', field: 'study_name' });
  const [testSubject, setTestSubject] = useState('');
  const [testResult, setTestResult] = useState(null);
  
  // Whitelist dialog
  const [showWhitelistDialog, setShowWhitelistDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();

      // Fetch config
      const configRes = await axios.get(`${API_URL}/api/email-ingestion/config`, { headers });
      setConfig(configRes.data);

      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/email-ingestion/stats`, { headers });
      setStats(statsRes.data);

      // Fetch logs
      const logsRes = await axios.get(`${API_URL}/api/email-ingestion/logs?limit=50`, { headers });
      setLogs(logsRes.data?.logs || []);

      // Fetch available targets
      const targetsRes = await axios.get(`${API_URL}/api/email-ingestion/available-targets`, { headers });
      setTargets(targetsRes.data);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load email ingestion settings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.put(`${API_URL}/api/email-ingestion/config`, config, { headers });
      toast.success('Configuration saved');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleIngestion = async (enabled) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/email-ingestion/toggle?enabled=${enabled}`, {}, { headers });
      setConfig(prev => ({ ...prev, enabled }));
      toast.success(`Email ingestion ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to toggle ingestion');
    }
  };

  const triggerCheck = async () => {
    setChecking(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/email-ingestion/check`, {}, { headers });
      
      if (response.data.status === 'success') {
        const results = response.data.results;
        toast.success(`Checked ${results.checked} emails, processed ${results.processed}, created ${results.documents_created} documents`);
        await fetchData();
      } else {
        toast.error(response.data.message || 'Check failed');
      }
    } catch (error) {
      toast.error('Failed to check emails');
    } finally {
      setChecking(false);
    }
  };

  const testPattern = async () => {
    if (!testSubject || !newPattern.pattern) {
      toast.error('Please enter both subject and pattern');
      return;
    }
    
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/email-ingestion/test-pattern`, null, {
        headers,
        params: { subject: testSubject, pattern: newPattern.pattern }
      });
      setTestResult(response.data);
    } catch (error) {
      toast.error('Invalid pattern');
      setTestResult({ matches: false, error: true });
    }
  };

  const addPattern = () => {
    if (!newPattern.pattern || !newPattern.field) return;
    
    setConfig(prev => ({
      ...prev,
      subject_patterns: [...(prev.subject_patterns || []), { ...newPattern }]
    }));
    setNewPattern({ pattern: '', field: 'study_name' });
    setTestSubject('');
    setTestResult(null);
    setShowPatternDialog(false);
  };

  const removePattern = (index) => {
    setConfig(prev => ({
      ...prev,
      subject_patterns: prev.subject_patterns.filter((_, i) => i !== index)
    }));
  };

  const addToWhitelist = async () => {
    if (!newEmail) return;
    
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/email-ingestion/whitelist/add`, null, {
        headers,
        params: { email: newEmail }
      });
      setConfig(prev => ({
        ...prev,
        allowed_senders: [...(prev.allowed_senders || []), newEmail]
      }));
      setNewEmail('');
      setShowWhitelistDialog(false);
      toast.success('Sender added to whitelist');
    } catch (error) {
      toast.error('Failed to add sender');
    }
  };

  const removeFromWhitelist = async (email) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/email-ingestion/whitelist/remove`, null, {
        headers,
        params: { email }
      });
      setConfig(prev => ({
        ...prev,
        allowed_senders: prev.allowed_senders.filter(e => e !== email)
      }));
      toast.success('Sender removed from whitelist');
    } catch (error) {
      toast.error('Failed to remove sender');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading email ingestion settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Email-to-eTMF Ingestion</h1>
                  <p className="text-xs text-white/60">Automatically import documents from email</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 mr-4">
                <span className="text-white/60 text-sm">Status:</span>
                <Badge className={config.enabled ? 'bg-green-500' : 'bg-slate-500'}>
                  {config.enabled ? 'Active' : 'Disabled'}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={triggerCheck}
                disabled={checking || !config.enabled}
                className="border-white/20 text-white hover:bg-slate-50/10"
                data-testid="check-emails-btn"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Check Emails Now
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="border-white/20 text-white hover:bg-slate-50/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-50/90 border-slate-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Ingested</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_ingested || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50/90 border-slate-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Last 24 Hours</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats?.last_24_hours || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50/90 border-slate-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Whitelisted Senders</p>
                  <p className="text-2xl font-bold text-slate-900">{config.allowed_senders?.length || 0}</p>
                </div>
                <Mail className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50/90 border-slate-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Last Check</p>
                  <p className="text-sm font-medium text-slate-900">
                    {stats?.last_check ? format(parseISO(stats.last_check), 'MMM dd, HH:mm') : 'Never'}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
            <TabsTrigger value="config" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-config">
              <Settings className="w-4 h-4 mr-2" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="patterns" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-patterns">
              <FileText className="w-4 h-4 mr-2" />
              Subject Patterns
            </TabsTrigger>
            <TabsTrigger value="whitelist" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-whitelist">
              <Mail className="w-4 h-4 mr-2" />
              Sender Whitelist
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-logs">
              <Clock className="w-4 h-4 mr-2" />
              Activity Log ({logs.length})
            </TabsTrigger>
          </TabsList>

          {/* Configuration Tab */}
          <TabsContent value="config" className="space-y-6" data-testid="config-content">
            <div className="grid grid-cols-2 gap-6">
              {/* Basic Settings */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Inbox className="w-5 h-5 text-emerald-600" />
                    Basic Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-900 font-medium">Enable Email Ingestion</Label>
                      <p className="text-sm text-slate-500">Automatically monitor inbox for documents</p>
                    </div>
                    <Switch
                      checked={config.enabled}
                      onCheckedChange={toggleIngestion}
                      data-testid="enable-ingestion-switch"
                    />
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-900 font-medium">Folder to Monitor</Label>
                      <Input
                        value={config.folder_to_monitor}
                        onChange={(e) => setConfig(prev => ({ ...prev, folder_to_monitor: e.target.value }))}
                        placeholder="INBOX"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-900 font-medium">Processed Folder</Label>
                      <Input
                        value={config.processed_folder}
                        onChange={(e) => setConfig(prev => ({ ...prev, processed_folder: e.target.value }))}
                        placeholder="Processed"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-900 font-medium">Default Workflow Status</Label>
                      <Select 
                        value={config.auto_workflow_status}
                        onValueChange={(value) => setConfig(prev => ({ ...prev, auto_workflow_status: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="pending_review">Pending Review</SelectItem>
                          <SelectItem value="in_review">In Review</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Target Settings */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Folder className="w-5 h-5 text-blue-600" />
                    Default Target
                  </CardTitle>
                  <CardDescription>Where to store ingested documents when not specified in email</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-900 font-medium">Default Study</Label>
                    <Select 
                      value={config.default_study_id || 'none'}
                      onValueChange={(value) => setConfig(prev => ({ 
                        ...prev, 
                        default_study_id: value === 'none' ? null : value,
                        default_folder_id: null
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a study" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {targets.studies.map(study => (
                          <SelectItem key={study.id} value={study.id}>{study.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {config.default_study_id && (
                    <div>
                      <Label className="text-slate-900 font-medium">Default Folder</Label>
                      <Select 
                        value={config.default_folder_id || 'auto'}
                        onValueChange={(value) => setConfig(prev => ({ ...prev, default_folder_id: value === 'auto' ? null : value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a folder" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">First folder in study</SelectItem>
                          {(targets.folders_by_study[config.default_study_id] || []).map(folder => (
                            <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-900 font-medium">Auto-classify Documents</Label>
                      <p className="text-sm text-slate-500">Automatically determine document type</p>
                    </div>
                    <Switch
                      checked={config.auto_classify}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, auto_classify: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-900 font-medium">Notify on Ingestion</Label>
                      <p className="text-sm text-slate-500">Send notification when documents are ingested</p>
                    </div>
                    <Switch
                      checked={config.notify_on_ingestion}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, notify_on_ingestion: checked }))}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end">
              <Button onClick={saveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Configuration
              </Button>
            </div>
          </TabsContent>

          {/* Patterns Tab */}
          <TabsContent value="patterns" className="space-y-4" data-testid="patterns-content">
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-slate-900">Subject Patterns</CardTitle>
                  <CardDescription>Extract metadata from email subjects using regex patterns</CardDescription>
                </div>
                <Button onClick={() => setShowPatternDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Pattern
                </Button>
              </CardHeader>
              <CardContent>
                {(!config.subject_patterns || config.subject_patterns.length === 0) ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600">No patterns configured</p>
                    <p className="text-sm text-slate-500">Add patterns to extract study, folder, or document type from email subjects</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {config.subject_patterns.map((pattern, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white">
                        <div>
                          <code className="bg-slate-100 px-2 py-1 rounded text-sm">{pattern.pattern}</code>
                          <Badge className="ml-2 bg-blue-100 text-blue-700">{pattern.field}</Badge>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removePattern(idx)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Pattern Examples</p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li><code>\[STUDY:([^\]]+)\]</code> - Matches [STUDY:Phase 3 Trial]</li>
                      <li><code>\[FOLDER:([^\]]+)\]</code> - Matches [FOLDER:Regulatory]</li>
                      <li><code>\[TYPE:([^\]]+)\]</code> - Matches [TYPE:Protocol Amendment]</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Whitelist Tab */}
          <TabsContent value="whitelist" className="space-y-4" data-testid="whitelist-content">
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-slate-900">Sender Whitelist</CardTitle>
                  <CardDescription>Only accept emails from these senders (empty = accept all)</CardDescription>
                </div>
                <Button onClick={() => setShowWhitelistDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Sender
                </Button>
              </CardHeader>
              <CardContent>
                {(!config.allowed_senders || config.allowed_senders.length === 0) ? (
                  <div className="text-center py-8">
                    <Mail className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600">No whitelist configured</p>
                    <p className="text-sm text-slate-500">Emails from all senders will be processed</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {config.allowed_senders.map((email, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-900">{email}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeFromWhitelist(email)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4" data-testid="logs-content">
            {logs.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <Clock className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Activity Yet</h3>
                  <p className="text-slate-600">Document ingestion activity will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {logs.map((log, idx) => (
                    <Card key={idx} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{log.entity_name}</p>
                              <p className="text-sm text-slate-500">{log.details}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-emerald-500/20 text-emerald-600">Ingested</Badge>
                            <p className="text-xs text-slate-500 mt-1">
                              {log.created_at ? format(parseISO(log.created_at), 'MMM dd, HH:mm') : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Pattern Dialog */}
      <Dialog open={showPatternDialog} onOpenChange={setShowPatternDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Subject Pattern</DialogTitle>
            <DialogDescription>Create a regex pattern to extract metadata from email subjects</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Regex Pattern</Label>
              <Input
                value={newPattern.pattern}
                onChange={(e) => setNewPattern(prev => ({ ...prev, pattern: e.target.value }))}
                placeholder="\[STUDY:([^\]]+)\]"
              />
            </div>
            <div>
              <Label>Extract To Field</Label>
              <Select 
                value={newPattern.field}
                onValueChange={(value) => setNewPattern(prev => ({ ...prev, field: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="study_name">Study Name</SelectItem>
                  <SelectItem value="folder_name">Folder Name</SelectItem>
                  <SelectItem value="document_type">Document Type</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Separator />
            
            <div>
              <Label>Test Subject</Label>
              <Input
                value={testSubject}
                onChange={(e) => setTestSubject(e.target.value)}
                placeholder="[STUDY:Phase 3] Protocol Amendment v2"
              />
            </div>
            <Button variant="outline" onClick={testPattern} className="w-full">
              Test Pattern
            </Button>
            
            {testResult && (
              <div className={`p-3 rounded-lg ${testResult.matches ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {testResult.matches ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span>Match! Captured: <strong>{testResult.captured}</strong></span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="w-4 h-4" />
                    <span>No match</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPatternDialog(false)}>Cancel</Button>
            <Button onClick={addPattern} disabled={!newPattern.pattern} className="bg-emerald-600 hover:bg-emerald-700">
              Add Pattern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Whitelist Dialog */}
      <Dialog open={showWhitelistDialog} onOpenChange={setShowWhitelistDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Add Sender to Whitelist</DialogTitle>
            <DialogDescription>Only emails from whitelisted senders will be processed</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Email Address</Label>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="sender@example.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWhitelistDialog(false)}>Cancel</Button>
            <Button onClick={addToWhitelist} disabled={!newEmail} className="bg-emerald-600 hover:bg-emerald-700">
              Add Sender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
